/**
 * React Query keys and functions for folders data
 *
 * This file centralizes all query keys and API functions for React Query.
 * Using query key factories ensures consistent cache keys across the app.
 */

import type { ContentType, Folder } from '@/db/Types.ts'
import { secureFetch, secureJsonFetch } from '@/utils/SecureFetch'

// ============================================================================
// QUERY KEY FACTORIES
// ============================================================================

export const folderKeys = {
	all: ['folders'] as const,
	list: () => ['folders', 'list'] as const,
	detail: (id: string) => ['folders', 'detail', id] as const,
	contents: (id: string) => ['folders', 'detail', id, 'contents'] as const,
}

// ============================================================================
// API FUNCTIONS
// ============================================================================

async function handleResponse<T>(response: Response): Promise<T> {
	if (!response.ok) {
		if (response.status === 401) {
			throw new Error('UNAUTHORIZED')
		}
		const error = await response.text()
		throw new Error(error || `HTTP ${response.status}`)
	}
	return response.json()
}

export async function fetchFolders(): Promise<Folder[]> {
	const response = await secureFetch('/api/folders')
	const data = await handleResponse<{ folders: Folder[] }>(response)
	return data.folders
}

export async function createFolder(data: {
	name: string
}): Promise<Folder> {
	const response = await secureJsonFetch('/api/folders', {
		method: 'POST',
		body: data,
	})
	const result = await handleResponse<{ folder: Folder }>(response)
	return result.folder
}

export async function updateFolder(
	id: string,
	data: { name: string },
): Promise<Folder> {
	const response = await secureJsonFetch(`/api/folders/${id}`, {
		method: 'PUT',
		body: data,
	})
	const result = await handleResponse<{ folder: Folder }>(response)
	return result.folder
}

export async function deleteFolder(id: string): Promise<void> {
	const response = await secureFetch(`/api/folders/${id}`, {
		method: 'DELETE',
	})
	if (!response.ok && response.status !== 401) {
		throw new Error(`Failed to delete folder: ${response.status}`)
	}
	if (response.status === 401) {
		throw new Error('UNAUTHORIZED')
	}
}

// ============================================================================
// FOLDER CONTENTS OPERATIONS
// ============================================================================

export interface FolderContentItem {
	type: ContentType
	id: string
}

/**
 * Add items to a folder.
 * Supports playbooks, presentations, and documents.
 */
export async function addItemsToFolder(
	folderId: string,
	items: FolderContentItem[]
): Promise<void> {
	const response = await secureJsonFetch(`/api/folders/${folderId}/contents`, {
		method: 'POST',
		body: { items },
	})

	if (!response.ok) {
		if (response.status === 401) {
			throw new Error('UNAUTHORIZED')
		}
		if (response.status === 403) {
			throw new Error('You do not have permission to add items to this folder')
		}
		if (response.status === 404) {
			throw new Error('Folder not found')
		}
		const error = await response.text()
		throw new Error(error || 'Failed to add items to folder')
	}
}

/**
 * Remove items from a folder.
 * Supports playbooks, presentations, and documents.
 */
export async function removeItemsFromFolder(
	folderId: string,
	items: FolderContentItem[]
): Promise<void> {
	const response = await secureJsonFetch(`/api/folders/${folderId}/contents`, {
		method: 'DELETE',
		body: { items },
	})

	if (!response.ok) {
		if (response.status === 401) {
			throw new Error('UNAUTHORIZED')
		}
		if (response.status === 403) {
			throw new Error('You do not have permission to remove items from this folder')
		}
		if (response.status === 404) {
			throw new Error('Folder not found')
		}
		const error = await response.text()
		throw new Error(error || 'Failed to remove items from folder')
	}
}

/**
 * Add items to multiple folders.
 * Convenience method for batch folder operations.
 */
export async function addItemsToFolders(
	folderIds: string[],
	items: FolderContentItem[]
): Promise<{ succeeded: string[]; failed: Array<{ folderId: string; error: string }> }> {
	const results = await Promise.allSettled(
		folderIds.map(async (folderId) => {
			await addItemsToFolder(folderId, items)
			return folderId
		})
	)

	const succeeded: string[] = []
	const failed: Array<{ folderId: string; error: string }> = []

	results.forEach((result, index) => {
		const folderId = folderIds[index]
		if (!folderId) return

		if (result.status === 'fulfilled') {
			succeeded.push(result.value)
		} else {
			failed.push({
				folderId,
				error: result.reason instanceof Error ? result.reason.message : 'Unknown error',
			})
		}
	})

	return { succeeded, failed }
}
